import type { AuthUser, IdeaBatch, GenerateResponse } from './types'

const TOKEN_KEY = 'ai_game_token'
const USER_KEY = 'ai_game_user'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}

export function setSession(token: string, user: AuthUser) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

export function patchUser(user: AuthUser) {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

function authHeaders(): HeadersInit {
  const token = getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function register(email: string, password: string) {
  const res = await fetch('/api/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  const data = (await res.json()) as { ok: boolean; token?: string; user?: AuthUser; error?: string }
  if (!res.ok || !data.ok || !data.token || !data.user) throw new Error(data.error || '注册失败')
  setSession(data.token, data.user)
  return data.user
}

export async function login(email: string, password: string) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  const data = (await res.json()) as { ok: boolean; token?: string; user?: AuthUser; error?: string }
  if (!res.ok || !data.ok || !data.token || !data.user) throw new Error(data.error || '登录失败')
  setSession(data.token, data.user)
  return data.user
}

export async function fetchMe(): Promise<AuthUser | null> {
  if (!getToken()) return null
  const res = await fetch('/api/auth/me', { headers: authHeaders() })
  if (res.status === 401) {
    clearSession()
    return null
  }
  const data = (await res.json()) as { ok: boolean; user?: AuthUser }
  if (!res.ok || !data.ok || !data.user) return null
  patchUser(data.user)
  return data.user
}

export async function unlockAdmin(password: string): Promise<AuthUser> {
  const res = await fetch('/api/auth/admin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ password }),
  })
  const data = (await res.json()) as { ok: boolean; user?: AuthUser; error?: string }
  if (!res.ok || !data.ok || !data.user) throw new Error(data.error || '解锁失败')
  patchUser(data.user)
  return data.user
}

export async function generateIdeaBatch(input: string): Promise<GenerateResponse> {
  const res = await fetch('/api/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ input }),
  })
  return (await res.json()) as GenerateResponse
}

export async function listBatches(): Promise<IdeaBatch[]> {
  const res = await fetch('/api/batches', { headers: authHeaders() })
  const data = (await res.json()) as { ok: boolean; batches?: IdeaBatch[]; error?: string }
  if (!res.ok || !data.ok) throw new Error(data.error || '加载历史失败')
  return data.batches || []
}

export async function getBatch(id: string): Promise<IdeaBatch> {
  const res = await fetch(`/api/batches/${id}`, { headers: authHeaders() })
  const data = (await res.json()) as { ok: boolean; batch?: IdeaBatch; error?: string }
  if (!res.ok || !data.ok || !data.batch) throw new Error(data.error || '记录不存在')
  return data.batch
}

async function pngBytesToJpegBase64(pngBytes: Uint8Array, quality = 0.9): Promise<{
  jpegBase64: string
  width: number
  height: number
}> {
  const blob = new Blob([pngBytes], { type: 'image/png' })
  const bitmap = await createImageBitmap(blob)
  const MAX_W = 1024
  const scale = Math.min(MAX_W / bitmap.width, 1)
  const w = Math.max(1, Math.round(bitmap.width * scale))
  const h = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('无法创建画布')
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(bitmap, 0, 0, w, h)
  bitmap.close?.()

  const jpegBlob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  )
  if (!jpegBlob) throw new Error('JPEG 转换失败')

  const buf = new Uint8Array(await jpegBlob.arrayBuffer())
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < buf.length; i += chunk) {
    binary += String.fromCharCode(...buf.subarray(i, i + chunk))
  }
  return { jpegBase64: btoa(binary), width: w, height: h }
}

/**
 * 生成一张概念图：
 * 1) 服务端只调百炼并把 PNG 交回（不做图像编码，避免 Worker 超 CPU 被 503）
 * 2) 浏览器把 PNG 转 JPEG 90%
 * 3) 上传 JPEG 由服务端校验入库
 */
export async function generateBatchImage(
  id: string,
  ideaIndex: number,
  retries = 2,
): Promise<GenerateResponse> {
  let last: GenerateResponse = { ok: false, error: '生图失败' }
  for (let attempt = 0; attempt <= retries; attempt++) {
    const res = await fetch(`/api/batches/${id}/image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ideaIndex }),
    })

    const contentType = res.headers.get('Content-Type') || ''

    // 1) PNG 二进制（正常路径）
    if (res.ok && contentType.includes('image/png')) {
      try {
        const pngBytes = new Uint8Array(await res.arrayBuffer())
        const { jpegBase64, width, height } = await pngBytesToJpegBase64(pngBytes, 0.9)
        const saveRes = await fetch(`/api/batches/${id}/image/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ ideaIndex, jpegBase64, width, height, quality: 90 }),
        })
        const saved = (await saveRes
          .json()
          .catch(() => ({ ok: false, error: '保存失败' }) as GenerateResponse)) as GenerateResponse
        if (saved.ok) return saved
        last = saved
        if ((saveRes.status >= 500 || saveRes.status === 429) && attempt < retries) {
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)))
          continue
        }
        return saved
      } catch (err) {
        last = { ok: false, error: err instanceof Error ? err.message : '图片转换失败' }
        if (attempt < retries) {
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)))
          continue
        }
        return last
      }
    }

    // 2) JSON（已存在 / 错误信息）
    const text = await res.text()
    let data: GenerateResponse
    try {
      data = JSON.parse(text) as GenerateResponse
    } catch {
      data = { ok: false, error: res.ok ? '服务器返回了非 JSON 响应' : `接口错误 HTTP ${res.status}` }
    }
    if (data.ok) return data
    last = data

    const retryable =
      res.status === 503 || res.status === 502 || res.status === 504 || res.status === 429
    if (!retryable || attempt === retries) return last
    await new Promise((r) => setTimeout(r, 900 * (attempt + 1)))
  }
  return last
}

export async function fetchBatchImage(id: string, ideaIndex = 0): Promise<string | null> {
  const res = await fetch(`/api/batches/${id}/image?idea=${ideaIndex}`, { headers: authHeaders() })
  if (!res.ok) return null
  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

export async function deleteBatch(id: string): Promise<void> {
  const res = await fetch(`/api/batches/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  const data = (await res.json()) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) throw new Error(data.error || '删除失败')
}
